import { useState } from 'react';
import { Link } from 'react-router-dom';

export default function HomePage() {
  const [flippedCard, setFlippedCard] = useState<string | null>(null);

  const handleCardFlip = (cardId: string) => {
    setFlippedCard(flippedCard === cardId ? null : cardId);
  };

  const services = [
    {
      id: 'strategic',
      title: 'Strategic Consulting',
      description: 'Market analysis, competitive intelligence, and strategic roadmapping for Life Sciences and Med-Tech companies',
      icon: 'ri-brain-line',
      links: [
        { name: 'EU & DE Scientific Patent Application', url: '#patent-application' },
        { name: 'Scientific Writing and Curation', url: '#scientific-writing' },
        { name: 'Technology Roadmapping', url: '#roadmapping' }
      ]
    },
    {
      id: 'regulatory',
      title: 'Regulatory Strategy',
      description: 'Navigate complex regulatory pathways for Health and Med-Tech devices and products',
      icon: 'ri-shield-check-line',
      links: [
        { name: 'FDA Strategy', url: '#fda-strategy' },
        { name: 'Clinical Trial Design', url: '#clinical-trials' },
        { name: 'Health Technology Assessment', url: '#hta' }
      ]
    },
    {
      id: 'investment',
      title: 'Investment Advisory',
      description: 'Due diligence and technology assessment for venture capital, Start-/Scale-up, and SME investments in the EU life sciences landscape.',
      icon: 'ri-line-chart-line',
      links: [
        { name: 'Non-Dilutive Funding Strategy', url: '#non-dilutive-funding' },
        { name: 'Med-Tech Trend Assessments', url: '#technology-trends' },
        { name: 'Cross-Border Innovation Facilitation', url: '#innovation-facilitation' }
      ]
    }
  ];

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    
    try {
      const response = await fetch('https://readdy.ai/api/form/d4g395lfv7b2bv2nghsg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formData as any).toString()
      });
      
      if (response.ok) {
        alert('Thank you for your message! I will get back to you soon.');
        form.reset();
      } else {
        alert('There was an error sending your message. Please try again.');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      alert('There was an error sending your message. Please try again.');
    }
  };

  return (
    <>
      <style>{`
        .flip-card {
          perspective: 1000px;
        }
        .flip-card-inner {
          position: relative;
          width: 100%;
          height: 100%;
          transition: transform 0.7s;
          transform-style: preserve-3d;
        }
        .flip-card.flipped .flip-card-inner {
          transform: rotateY(180deg);
        }
        .flip-card-front, .flip-card-back {
          position: absolute;
          width: 100%;
          height: 100%;
          -webkit-backface-visibility: hidden;
          backface-visibility: hidden;
        }
        .flip-card-back {
          transform: rotateY(180deg);
        }
      `}</style>
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
        {/* Navigation */}
        <nav className="bg-white/90 backdrop-blur-sm shadow-sm sticky top-0 z-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-16">
              <div className="flex items-center">
                <h1 className="text-lg md:text-2xl font-bold bg-gradient-to-r from-blue-600 to-teal-600 bg-clip-text text-transparent whitespace-nowrap">
                  Dr. Tina Ghelani
                </h1>
              </div>
              {/* Mobile Navigation Links */}
              <div className="flex md:hidden items-center space-x-3 text-xs">
                <Link to="/about" className="text-gray-700 hover:text-blue-600 transition-colors whitespace-nowrap cursor-pointer">About</Link>
                <Link to="/projects" className="text-gray-700 hover:text-blue-600 transition-colors whitespace-nowrap cursor-pointer">Experience</Link>
                <a href="#services" className="text-gray-700 hover:text-blue-600 transition-colors whitespace-nowrap cursor-pointer">Services</a>
              </div>
              {/* Desktop Navigation */}
              <div className="hidden md:flex items-center space-x-8">
                <Link to="/about" className="text-gray-700 hover:text-blue-600 transition-colors whitespace-nowrap cursor-pointer">About</Link>
                <a href="#services" className="text-gray-700 hover:text-blue-600 transition-colors whitespace-nowrap cursor-pointer">Services</a>
                <Link to="/projects" className="text-gray-700 hover:text-blue-600 transition-colors whitespace-nowrap cursor-pointer">Experience</Link>
                <a href="#contact" className="text-gray-700 hover:text-blue-600 transition-colors whitespace-nowrap cursor-pointer">Contact</a>
                <a 
                  href="https://calendar.app.google/6szNfxS4wtq3RLBC7"
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="bg-teal-500 hover:bg-teal-600 text-white px-4 py-2 rounded-lg font-medium transition-colors whitespace-nowrap cursor-pointer"
                >
                  Book Consultation
                </a>
                <a href="https://www.linkedin.com/in/dr-tina-ghelani" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 transition-colors cursor-pointer">
                  <i className="ri-linkedin-fill text-xl"></i>
                </a>
              </div>
            </div>
          </div>
        </nav>

        {/* Hero Section */}
        <section className="relative overflow-hidden bg-gradient-to-r from-blue-900 via-blue-800 to-teal-800 text-white">
          <div 
            className="absolute inset-0 bg-cover bg-center bg-no-repeat"
            style={{
              backgroundImage: "url('https://static.readdy.ai/image/cdda583d3b14aa1daf8ea5ccc5d6b52c/b7a5d9d602a0f6af67570c44f5839988.png')"
            }}
          >
            <div className="absolute inset-0 bg-gradient-to-r from-blue-900/90 via-blue-800/85 to-transparent"></div>
          </div>
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 lg:py-32">
            <div className="w-full">
              <div className="max-w-3xl">
                <h2 className="text-2xl md:text-5xl lg:text-5xl font-bold mb-6 leading-tight">
                  Advancing <span className="text-teal-300">Lifescience Innovation</span> Through Expert Consulting
                </h2>
                <p className="text-sm md:text-xl lg:text-2xl mb-8 text-blue-100 leading-relaxed">
                  Strategic life sciences consulting with deep neuroscience expertise. Specializing in regulatory strategy, innovation guidance, and science policy for Med-Tech startups and research institutions.
                </p>
                <div className="flex flex-col sm:flex-row gap-4">
                  <a 
                    href="https://calendar.app.google/6szNfxS4wtq3RLBC7"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-teal-500 hover:bg-teal-600 text-white text-sm md:text-base px-8 py-4 rounded-lg font-semibold transition-colors text-center whitespace-nowrap cursor-pointer"
                  >
                    Schedule Consultation
                  </a>
                  <a href="#contact" className="border-2 border-white text-white hover:bg-white hover:text-blue-900 text-sm md:text-base px-8 py-4 rounded-lg font-semibold transition-colors text-center whitespace-nowrap cursor-pointer">
                    Get In Touch
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* About Section */}
        <section id="about" className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <h3 className="text-xl md:text-3xl font-bold text-gray-900 mb-6 text-center">
                  Leading Innovation in Neuroscience and Life Sciences
                </h3>
                <p className="text-xs md:text-lg text-gray-600 mb-6 leading-relaxed text-justify">
                  An interdisciplinary Senior Neuroscientist with extensive experience in neuroscientific and medical technology innovations, as well as intellectual property ideation. Over 14 years of expertise in utilizing cutting-edge tools from biotechnology, cellular and molecular biology, biophysics, and optics in neuroscience research. I have extensive expertise in <strong>neuroscience consulting</strong>, regulatory strategy, and science policy. 
                </p>
                <p className="text-xs md:text-lg text-gray-600 mb-8 leading-relaxed text-justify">
                  Published in prestigious journals including <strong>PNAS, Nature Communications, and Science Advances</strong>. Recognized as an <strong>Indo-German Young Leader 2024</strong> and invited expert for science innovation policy at Konrad Adenauer Stiftung (KAS), Berlin. Awarded <strong>2x DFG grants</strong> for cutting-edge neuroscience <em>in vivo</em> single-molecule imaging research.
                </p>
                <div className="flex gap-4">
                  <a 
                    href="https://calendar.app.google/6szNfxS4wtq3RLBC7"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-gradient-to-r from-blue-600 to-teal-600 text-white text-sm md:text-base px-6 py-3 rounded-lg font-semibold hover:from-blue-700 hover:to-teal-700 transition-all whitespace-nowrap cursor-pointer"
                  >
                    Book a Meeting
                  </a>
                  <a 
                    href="https://www.linkedin.com/in/dr-tina-ghelani"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="border-2 border-blue-600 text-blue-600 hover:bg-blue-600 hover:text-white text-sm md:text-base px-6 py-3 rounded-lg font-semibold transition-colors whitespace-nowrap cursor-pointer"
                  >
                    LinkedIn Profile
                  </a>
                </div>
              </div>
              <div className="relative">
                <img
                  src="https://static.readdy.ai/image/cdda583d3b14aa1daf8ea5ccc5d6b52c/3051644fee142dcb58140251562fcd51.jpeg"
                  alt="Dr. Tina Ghelani - Neuroscience consultant and EU HTA regulator"
                  title="Dr. Tina Ghelani - Expert neuroscience consulting services"
                  className="rounded-2xl shadow-2xl object-cover w-full h-96"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Services Section with Flip Cards */}
        <section id="services" className="py-20 bg-gradient-to-br from-gray-50 to-blue-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h3 className="text-xl md:text-3xl font-bold text-gray-900 mb-4">
                Specialized Consulting Services
              </h3>
              <p className="text-sm md:text-xl text-gray-600 max-w-3xl mx-auto">
                Comprehensive neuroscience expertise across strategic planning, regulatory navigation, and investment advisory
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {services.map((service) => (
                <div key={service.id} className={`flip-card h-80 ${flippedCard === service.id ? 'flipped' : ''}`}>
                  <div 
                    className="flip-card-inner cursor-pointer"
                    onClick={() => handleCardFlip(service.id)}
                  >
                    {/* Front of card */}
                    <div className="flip-card-front bg-white rounded-xl shadow-lg p-8 flex flex-col items-center text-center">
                      <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-teal-500 rounded-full flex items-center justify-center mb-6">
                        <i className={`${service.icon} text-2xl text-white`}></i>
                      </div>
                      <h4 className="text-base md:text-xl font-bold text-gray-900 mb-4">{service.title}</h4>
                      <p className="text-xs md:text-base text-gray-600 mb-6 flex-1">{service.description}</p>
                      <div className="text-xs md:text-sm text-blue-600 font-medium">Click to see services →</div>
                    </div>
                    
                    {/* Back of card */}
                    <div className="flip-card-back bg-gradient-to-br from-blue-600 to-teal-600 rounded-xl shadow-lg p-8 flex flex-col justify-center text-white">
                      <h4 className="text-base md:text-xl font-bold mb-6 text-center">{service.title}</h4>
                      <div className="space-y-4">
                        {service.links.map((link, index) => (
                          <a
                            key={index}
                            href={link.url}
                            className="block p-3 bg-white/20 rounded-lg hover:bg-white/30 transition-colors text-center text-xs md:text-base whitespace-nowrap cursor-pointer"
                          >
                            {link.name}
                          </a>
                        ))}
                      </div>
                      <div className="text-xs md:text-sm text-center mt-6 opacity-80">Click to flip back</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Experience CTA */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h3 className="text-xl md:text-3xl font-bold text-gray-900 mb-6">
              Proven Track Record in Neuroscience & Life Sciences
            </h3>
            <p className="text-sm md:text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
              Explore detailed experience including consulting engagements, regulatory roles, publications, and honors across neuroscience and life sciences innovation.
            </p>
            <Link 
              to="/projects"
              className="bg-gradient-to-r from-blue-600 to-teal-600 text-white text-sm md:text-base px-8 py-4 rounded-lg font-semibold hover:from-blue-700 hover:to-teal-700 transition-all whitespace-nowrap cursor-pointer inline-block"
            >
              View Full Experience
            </Link>
          </div>
        </section>

        {/* Contact Section */}
        <section id="contact" className="py-20 bg-gradient-to-br from-gray-900 to-blue-900 text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              <div>
                <h3 className="text-xl md:text-3xl font-bold mb-6">Let's Discuss Your Neuroscience Needs</h3>
                <p className="text-sm md:text-xl text-gray-300 mb-8">
                  Ready to advance your neuroscience initiatives? Schedule a consultation to explore how strategic consulting can accelerate your success in this dynamic field.
                </p>
                
                <div className="space-y-6">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-teal-500 rounded-full flex items-center justify-center">
                      <i className="ri-calendar-line text-xl text-white"></i>
                    </div>
                    <div>
                      <div className="text-sm md:text-base font-semibold">Book Consultation</div>
                      <a 
                        href="https://calendar.app.google/6szNfxS4wtq3RLBC7"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs md:text-base text-teal-300 hover:text-teal-200 transition-colors cursor-pointer"
                      >
                        Schedule a Meeting
                      </a>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center">
                      <i className="ri-linkedin-fill text-xl text-white"></i>
                    </div>
                    <div>
                      <div className="text-sm md:text-base font-semibold">Professional Network</div>
                      <a href="https://www.linkedin.com/in/dr-tina-ghelani" target="_blank" rel="noopener noreferrer" className="text-xs md:text-base text-teal-300 hover:text-teal-200 transition-colors cursor-pointer">
                        Connect on LinkedIn
                      </a>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-purple-500 rounded-full flex items-center justify-center">
                      <i className="ri-mail-line text-xl text-white"></i>
                    </div>
                    <div>
                      <div className="text-sm md:text-base font-semibold">Email Consultation</div>
                      <div className="text-xs md:text-base text-gray-300">Use the form to get started</div>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8">
                <form onSubmit={handleSubmit} data-readdy-form id="contact-form">
                  <input type="hidden" name="recipient_email" value="tinaghelani@gmail.com" />
                  <div className="space-y-6">
                    <div>
                      <label htmlFor="name" className="block text-xs md:text-sm font-medium mb-2">Name *</label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        required
                        className="w-full px-4 py-3 text-sm md:text-base bg-white/20 border border-white/30 rounded-lg text-white placeholder-gray-300 focus:outline-none focus:ring-2 focus:ring-teal-500"
                        placeholder="Your full name"
                      />
                    </div>
                    
                    <div>
                      <label htmlFor="email" className="block text-xs md:text-sm font-medium mb-2">Email *</label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        required
                        className="w-full px-4 py-3 text-sm md:text-base bg-white/20 border border-white/30 rounded-lg text-white placeholder-gray-300 focus:outline-none focus:ring-2 focus:ring-teal-500"
                        placeholder="your@email.com"
                      />
                    </div>
                    
                    <div>
                      <label htmlFor="company" className="block text-xs md:text-sm font-medium mb-2">Company</label>
                      <input
                        type="text"
                        id="company"
                        name="company"
                        className="w-full px-4 py-3 text-sm md:text-base bg-white/20 border border-white/30 rounded-lg text-white placeholder-gray-300 focus:outline-none focus:ring-2 focus:ring-teal-500"
                        placeholder="Your organization"
                      />
                    </div>
                    
                    <div>
                      <label htmlFor="project_type" className="block text-xs md:text-sm font-medium mb-2">Project Type</label>
                      <select
                        id="project_type"
                        name="project_type"
                        className="w-full px-4 py-3 pr-8 text-sm md:text-base bg-white/20 border border-white/30 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-teal-500"
                      >
                        <option value="" className="text-gray-900">Select project type</option>
                        <option value="strategic" className="text-gray-900">Strategic Consulting</option>
                        <option value="regulatory" className="text-gray-900">Regulatory Strategy</option>
                        <option value="investment" className="text-gray-900">Investment Advisory</option>
                        <option value="other" className="text-gray-900">Other</option>
                      </select>
                    </div>
                    
                    <div>
                      <label htmlFor="message" className="block text-xs md:text-sm font-medium mb-2">Message *</label>
                      <textarea
                        id="message"
                        name="message"
                        required
                        maxLength={500}
                        rows={4}
                        className="w-full px-4 py-3 text-sm md:text-base bg-white/20 border border-white/30 rounded-lg text-white placeholder-gray-300 focus:outline-none focus:ring-2 focus:ring-teal-500 resize-none"
                        placeholder="Describe your neuroscience consulting needs..."
                      ></textarea>
                      <div className="text-xs text-gray-400 mt-1">Maximum 500 characters</div>
                    </div>
                    
                    <button
                      type="submit"
                      className="w-full bg-gradient-to-r from-teal-500 to-blue-500 hover:from-teal-600 hover:to-blue-600 text-white text-sm md:text-base font-semibold py-4 rounded-lg transition-all whitespace-nowrap cursor-pointer"
                    >
                      Send Message
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="bg-gray-900 text-white py-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div>
                <h4 className="text-xl md:text-2xl font-bold bg-gradient-to-r from-blue-400 to-teal-400 bg-clip-text text-transparent mb-4">
                  Dr. Tina Ghelani
                </h4>
                <p className="text-xs md:text-base text-gray-400 mb-4">
                  Expert life sciences consulting with neuroscience specialization
                </p>
                <a href="https://www.linkedin.com/in/dr-tina-ghelani" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300 transition-colors cursor-pointer">
                  <i className="ri-linkedin-fill text-2xl"></i>
                </a>
              </div>
              
              <div>
                <h5 className="text-sm md:text-base font-semibold mb-4">Services</h5>
                <ul className="space-y-2 text-xs md:text-base text-gray-400">
                  <li><a href="#strategic" className="hover:text-white transition-colors cursor-pointer">Strategic Consulting</a></li>
                  <li><a href="#regulatory" className="hover:text-white transition-colors cursor-pointer">Regulatory Strategy</a></li>
                  <li><a href="#investment" className="hover:text-white transition-colors cursor-pointer">Investment Advisory</a></li>
                </ul>
              </div>
              
              <div>
                <h5 className="text-sm md:text-base font-semibold mb-4">Quick Links</h5>
                <ul className="space-y-2 text-xs md:text-base text-gray-400">
                  <li><Link to="/about" className="hover:text-white transition-colors cursor-pointer">About</Link></li>
                  <li><Link to="/projects" className="hover:text-white transition-colors cursor-pointer">Experience</Link></li>
                  <li><a href="#contact" className="hover:text-white transition-colors cursor-pointer">Contact</a></li>
                  <li>
                    <a 
                      href="https://calendar.app.google/6szNfxS4wtq3RLBC7"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="hover:text-white transition-colors cursor-pointer"
                    >
                      Book Consultation
                    </a>
                  </li>
                </ul>
              </div>
            </div>
            
            <div className="border-t border-gray-800 mt-8 pt-8 text-center text-xs md:text-base text-gray-400">
              <p>&copy; 2024 Dr. Tina Ghelani. All rights reserved. | <a href="https://readdy.ai/?origin=logo" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors cursor-pointer">Powered by Readdy</a></p>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
}
